import {
  deleteAdminShopCrate,
  fetchAdminShopCosmetics,
  fetchAdminShopCrates,
  publishAdminShopCrate,
  patchAdminShopCrate,
  type AdminShopCosmeticRow,
  type AdminShopCrateRow,
  type ShopUnlockType,
} from "../lib/cloud";
import { mountAdminCrateArtEditor } from "./admin-shop-creator";
import { createModalCloseButton } from "./modal-close-button";
import { mountAdminCrateItems } from "./admin-crate-items";

const SCORE_UNLOCK_MODES: Array<{ id: string; label: string }> = [
  { id: "normal", label: "Normal" },
  { id: "wasd", label: "WASD" },
  { id: "superdot", label: "Superdot" },
  { id: "progression", label: "Progression" },
  { id: "roulette", label: "Roulette" },
  { id: "sandbox", label: "Sandbox" },
  { id: "speedrun", label: "SpeedRun" },
  { id: "highway", label: "Highway" },
  { id: "darkness", label: "Darkness" },
  { id: "mirror", label: "Mirror" },
  { id: "snake", label: "Snake" },
  { id: "combo", label: "Combo" },
  { id: "overload", label: "Overload" },
  { id: "colorrush", label: "Color Rush" },
  { id: "heat", label: "Heat" },
  { id: "hunter", label: "Hunter" },
  { id: "pop", label: "POP" },
  { id: "counter", label: "Counter" },
  { id: "aim", label: "AIM" },
  { id: "hook", label: "Hook" },
  { id: "tilt", label: "Tilt" },
  { id: "orbit", label: "Orbit" },
  { id: "delay", label: "Delay" },
  { id: "penalty", label: "Golf" },
];

function formatPercent(n: number) {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

const CRATE_PROB_STEP_BP = 100;

type CrateContentLocal = {
  cosmeticId: string;
  cosmeticName: string;
  cosmeticCategory: string;
  cosmeticImageData: string;
  cosmeticOutlineMaskData: string | null;
  cosmeticAnimationFrames: string[] | null;
  cosmeticMatchCursorColor: boolean;
  cosmeticCursorColorShade: number;
  cosmeticMatchCursorMaskData: string | null;
  cosmeticMatchCursorMaskFrames: string[] | null;
  cosmeticNewCursor?: boolean;
  probabilityPercent: number;
};

function percentToBasisPoints(percent: number) {
  return Math.max(0, Math.round((Number(percent) || 0) * 100));
}

function basisPointsToPercent(bp: number) {
  return bp / 100;
}

function fixBasisPointsTotal(bp: number[]) {
  const total = bp.reduce((sum, value) => sum + value, 0);
  if (!bp.length || total === 10_000) return;
  const diff = 10_000 - total;
  const maxIdx = bp.reduce((best, value, index, arr) => (value > arr[best] ? index : best), 0);
  bp[maxIdx] = Math.max(0, bp[maxIdx] + diff);
}

function equalizeContentsProbabilities(contents: Array<{ probabilityPercent: number }>) {
  const n = contents.length;
  if (!n) return;
  const base = Math.floor(10_000 / n);
  let remainder = 10_000 - base * n;
  contents.forEach((item) => {
    const extra = remainder > 0 ? 1 : 0;
    if (remainder > 0) remainder -= 1;
    item.probabilityPercent = basisPointsToPercent(base + extra);
  });
}

function ensureContentsProbabilities(contents: Array<{ probabilityPercent: number }>) {
  if (!contents.length) return;
  const bp = contents.map((item) => percentToBasisPoints(item.probabilityPercent));
  const total = bp.reduce((sum, value) => sum + value, 0);
  if (total <= 0) {
    equalizeContentsProbabilities(contents);
    return;
  }
  if (total !== 10_000) {
    const normalized = bp.map((value) => Math.round((value * 10_000) / total));
    fixBasisPointsTotal(normalized);
    contents.forEach((item, index) => {
      item.probabilityPercent = basisPointsToPercent(normalized[index]);
    });
  }
}

function distributeBasisPoints(bp: number[], indices: number[], amount: number, sign: 1 | -1) {
  if (!indices.length || amount <= 0) return;
  const base = Math.floor(amount / indices.length);
  let extra = amount - base * indices.length;
  for (const index of indices) {
    const delta = base + (extra > 0 ? 1 : 0);
    if (extra > 0) extra -= 1;
    if (sign === 1) bp[index] += delta;
    else bp[index] = Math.max(0, bp[index] - delta);
  }
}

function adjustContentProbability(
  contents: Array<{ probabilityPercent: number }>,
  index: number,
  direction: "up" | "down",
) {
  if (contents.length < 2 || index < 0 || index >= contents.length) return;
  ensureContentsProbabilities(contents);
  const bp = contents.map((item) => percentToBasisPoints(item.probabilityPercent));
  const others = bp.map((_, i) => i).filter((i) => i !== index);
  if (direction === "up") {
    const othersTotal = others.reduce((sum, i) => sum + bp[i], 0);
    if (othersTotal < CRATE_PROB_STEP_BP) return;
    bp[index] += CRATE_PROB_STEP_BP;
    distributeBasisPoints(bp, others, CRATE_PROB_STEP_BP, -1);
  } else {
    if (bp[index] < CRATE_PROB_STEP_BP) return;
    bp[index] -= CRATE_PROB_STEP_BP;
    distributeBasisPoints(bp, others, CRATE_PROB_STEP_BP, 1);
  }
  fixBasisPointsTotal(bp);
  contents.forEach((item, i) => {
    item.probabilityPercent = basisPointsToPercent(bp[i]);
  });
}

function unlockOptionLabel(v: ShopUnlockType) {
  switch (v) {
    case "dots":
      return "Price in Dots";
    case "free":
      return "Free";
    case "achievement":
      return "Achievement unlock";
    case "challenge":
      return "Challenge unlock";
    case "score":
      return "Score in mode";
  }
}

export function mountAdminShopCrates(
  root: HTMLElement,
  opts?: {
    onEditCrateItem?: (item: AdminShopCosmeticRow) => void;
    permissions?: { overwrite?: boolean; delete?: boolean };
  },
) {
  root.replaceChildren();

  const topBar = document.createElement("div");
  topBar.className = "adminShopItemsTabs modeBox";
  const refreshBtn = document.createElement("button");
  refreshBtn.type = "button";
  refreshBtn.className = "btn green";
  refreshBtn.textContent = "Refresh";
  const createBtn = document.createElement("button");
  createBtn.type = "button";
  createBtn.className = "btn";
  createBtn.textContent = "Create Crate";
  const crateItemsBtn = document.createElement("button");
  crateItemsBtn.type = "button";
  crateItemsBtn.className = "btn";
  crateItemsBtn.textContent = "Crates Items";
  topBar.appendChild(refreshBtn);
  topBar.appendChild(createBtn);
  topBar.appendChild(crateItemsBtn);
  root.appendChild(topBar);

  const cratesPanel = document.createElement("div");
  cratesPanel.className = "adminShopCratesPanel";
  const itemsPanel = document.createElement("div");
  itemsPanel.className = "adminShopCratesItemsPanel hidden";
  root.appendChild(cratesPanel);
  root.appendChild(itemsPanel);

  const status = document.createElement("p");
  status.className = "accessHint";
  cratesPanel.appendChild(status);

  const list = document.createElement("div");
  list.className = "shopCategorySection";
  cratesPanel.appendChild(list);

  const crateItemsMount = document.createElement("div");
  itemsPanel.appendChild(crateItemsMount);
  const crateItemsApi = mountAdminCrateItems(crateItemsMount, {
    onEditItem: (item) => opts?.onEditCrateItem?.(item),
    permissions: opts?.permissions,
  });

  let view: "crates" | "items" = "crates";
  function setView(next: "crates" | "items") {
    view = next;
    refreshBtn.classList.toggle("green", view === "crates");
    crateItemsBtn.classList.toggle("green", view === "items");
    cratesPanel.classList.toggle("hidden", view !== "crates");
    itemsPanel.classList.toggle("hidden", view !== "items");
    if (view === "items") void crateItemsApi.refresh();
    else void load();
  }

  let crates: AdminShopCrateRow[] = [];
  let cosmetics: AdminShopCosmeticRow[] = [];

  function syncUnlockVisibility(
    unlock: HTMLSelectElement,
    priceRow: HTMLElement,
    achievementRow: HTMLElement,
    challengeRow: HTMLElement,
    modeRow: HTMLElement,
    scoreRow: HTMLElement,
  ) {
    const t = unlock.value as ShopUnlockType;
    priceRow.classList.toggle("hidden", t !== "dots");
    achievementRow.classList.toggle("hidden", t !== "achievement");
    challengeRow.classList.toggle("hidden", t !== "challenge");
    modeRow.classList.toggle("hidden", t !== "score");
    scoreRow.classList.toggle("hidden", t !== "score");
  }

  function openCrateArtEditor(crate: AdminShopCrateRow) {
    const overlay = document.createElement("div");
    overlay.className = "adminShopPublishModal screen";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    const panel = document.createElement("div");
    panel.className = "modeDetailModalPanel modeBox";
    panel.style.width = "min(960px, 96vw)";
    panel.style.maxWidth = "960px";
    panel.appendChild(createModalCloseButton(() => overlay.remove(), "Close crate art editor"));
    const title = document.createElement("h2");
    title.textContent = `Edit art — ${crate.name}`;
    panel.appendChild(title);
    const mountEl = document.createElement("div");
    panel.appendChild(mountEl);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    const api = mountAdminCrateArtEditor(mountEl, {
      onCrateArtSaved: () => {
        overlay.remove();
        void load();
      },
    });
    api.loadCrate({ id: crate.id, name: crate.name, imageData: crate.imageData });
  }

  function render() {
    list.replaceChildren();
    if (!crates.length) {
      const empty = document.createElement("p");
      empty.className = "accessHint";
      empty.textContent = "No crate drafts yet. Use Create Create to add one.";
      list.appendChild(empty);
      return;
    }

    for (const crate of crates) {
      const card = document.createElement("section");
      card.className = "modeBox";
      card.style.marginBottom = "14px";

      const head = document.createElement("div");
      head.className = "rgbRow";
      head.style.alignItems = "flex-start";
      head.style.gap = "14px";

      const preview = document.createElement("img");
      preview.src = crate.imageData;
      preview.alt = crate.name;
      preview.style.width = "120px";
      preview.style.height = "120px";
      preview.style.imageRendering = "pixelated";
      preview.style.flex = "0 0 auto";
      preview.style.border = "1px solid rgba(255,255,255,.2)";
      preview.style.background = "rgba(0,0,0,.28)";
      preview.style.borderRadius = "12px";
      head.appendChild(preview);

      const meta = document.createElement("div");
      meta.style.flex = "1";

      const nameInput = document.createElement("input");
      nameInput.className = "formInput";
      nameInput.value = crate.name;
      nameInput.placeholder = "Crate name";
      meta.appendChild(nameInput);

      const badges = document.createElement("p");
      badges.className = "accessHint";
      badges.textContent = `${crate.isPublished ? "Published" : "Draft"} • ${
        crate.isConfigured ? "Configured" : "Unconfigured"
      } • ${localContentsLabel(crate.contents.length)}`;
      meta.appendChild(badges);

      head.appendChild(meta);
      card.appendChild(head);

      const unlockWrap = document.createElement("div");
      unlockWrap.className = "adminShopItemOpts";
      unlockWrap.style.marginTop = "12px";

      const unlockRow = document.createElement("div");
      unlockRow.className = "formRow";
      const unlockLabel = document.createElement("span");
      unlockLabel.className = "formLabel";
      unlockLabel.textContent = "Unlock";
      const unlockSelect = document.createElement("select");
      unlockSelect.className = "formInput";
      (["dots", "free", "achievement", "challenge", "score"] as ShopUnlockType[]).forEach((v) => {
        const o = document.createElement("option");
        o.value = v;
        o.textContent = unlockOptionLabel(v);
        unlockSelect.appendChild(o);
      });
      unlockSelect.value = crate.unlockType;
      unlockRow.appendChild(unlockLabel);
      unlockRow.appendChild(unlockSelect);
      unlockWrap.appendChild(unlockRow);

      const priceRow = document.createElement("div");
      priceRow.className = "formRow";
      const priceLabel = document.createElement("span");
      priceLabel.className = "formLabel";
      priceLabel.textContent = "Price";
      const priceInput = document.createElement("input");
      priceInput.type = "number";
      priceInput.min = "0";
      priceInput.step = "1";
      priceInput.className = "formInput";
      priceInput.value = String(crate.priceDots ?? 0);
      priceRow.appendChild(priceLabel);
      priceRow.appendChild(priceInput);
      unlockWrap.appendChild(priceRow);

      const achievementRow = document.createElement("div");
      achievementRow.className = "formRow";
      const achievementLabel = document.createElement("span");
      achievementLabel.className = "formLabel";
      achievementLabel.textContent = "Achievement";
      const achievementInput = document.createElement("input");
      achievementInput.className = "formInput";
      achievementInput.value = crate.unlockAchievementId ?? "";
      achievementInput.placeholder = "Achievement id";
      achievementRow.appendChild(achievementLabel);
      achievementRow.appendChild(achievementInput);
      unlockWrap.appendChild(achievementRow);

      const challengeRow = document.createElement("div");
      challengeRow.className = "formRow";
      const challengeLabel = document.createElement("span");
      challengeLabel.className = "formLabel";
      challengeLabel.textContent = "Challenge";
      const challengeInput = document.createElement("input");
      challengeInput.className = "formInput";
      challengeInput.value = crate.unlockChallengeId ?? "";
      challengeInput.placeholder = "Challenge id";
      challengeRow.appendChild(challengeLabel);
      challengeRow.appendChild(challengeInput);
      unlockWrap.appendChild(challengeRow);

      const modeRow = document.createElement("div");
      modeRow.className = "formRow";
      const modeLabel = document.createElement("span");
      modeLabel.className = "formLabel";
      modeLabel.textContent = "Mode";
      const modeSelect = document.createElement("select");
      modeSelect.className = "formInput";
      SCORE_UNLOCK_MODES.forEach(({ id, label }) => {
        const o = document.createElement("option");
        o.value = id;
        o.textContent = label;
        modeSelect.appendChild(o);
      });
      modeSelect.value = crate.unlockScoreMode || SCORE_UNLOCK_MODES[0]?.id || "normal";
      modeRow.appendChild(modeLabel);
      modeRow.appendChild(modeSelect);
      unlockWrap.appendChild(modeRow);

      const scoreRow = document.createElement("div");
      scoreRow.className = "formRow";
      const scoreLabel = document.createElement("span");
      scoreLabel.className = "formLabel";
      scoreLabel.textContent = "Score";
      const scoreInput = document.createElement("input");
      scoreInput.type = "number";
      scoreInput.min = "1";
      scoreInput.step = "1";
      scoreInput.className = "formInput";
      scoreInput.value = String(crate.unlockScoreValue ?? 1);
      scoreRow.appendChild(scoreLabel);
      scoreRow.appendChild(scoreInput);
      unlockWrap.appendChild(scoreRow);

      unlockSelect.onchange = () =>
        syncUnlockVisibility(unlockSelect, priceRow, achievementRow, challengeRow, modeRow, scoreRow);
      syncUnlockVisibility(unlockSelect, priceRow, achievementRow, challengeRow, modeRow, scoreRow);
      card.appendChild(unlockWrap);

      const contentsTitle = document.createElement("h4");
      contentsTitle.textContent = "Contents";
      card.appendChild(contentsTitle);

      const contentsWrap = document.createElement("div");
      contentsWrap.className = "leaderboardWrap";
      const table = document.createElement("table");
      table.className = "leaderboardTable";
      table.innerHTML = "<thead><tr><th>Item</th><th>Chance</th><th></th></tr></thead>";
      const tbody = document.createElement("tbody");
      table.appendChild(tbody);
      contentsWrap.appendChild(table);
      card.appendChild(contentsWrap);

      const localContents: CrateContentLocal[] = crate.contents.map((item) => ({ ...item }));
      ensureContentsProbabilities(localContents);
      const rerenderContents = () => {
        tbody.replaceChildren();
        badges.textContent = `${crate.isPublished ? "Published" : "Draft"} • ${
          localContents.length > 0 ? "Configured" : "Unconfigured"
        } • ${localContentsLabel(localContents.length)}`;
        localContents.forEach((item, index) => {
          const tr = document.createElement("tr");
          const tdItem = document.createElement("td");
          tdItem.textContent = item.cosmeticName;
          const tdProb = document.createElement("td");
          tdProb.textContent = `${formatPercent(item.probabilityPercent)}%`;
          const tdAct = document.createElement("td");
          const probBtns = document.createElement("div");
          probBtns.className = "adminCrateProbBtns";

          const upBtn = document.createElement("button");
          upBtn.type = "button";
          upBtn.className = "btn adminCrateProbBtn";
          upBtn.title = "Increase drop chance";
          upBtn.setAttribute("aria-label", `Increase chance for ${item.cosmeticName}`);
          upBtn.textContent = "▲";
          upBtn.disabled = localContents.length < 2;
          upBtn.onclick = () => {
            adjustContentProbability(localContents, index, "up");
            rerenderContents();
          };

          const downBtn = document.createElement("button");
          downBtn.type = "button";
          downBtn.className = "btn adminCrateProbBtn";
          downBtn.title = "Decrease drop chance";
          downBtn.setAttribute("aria-label", `Decrease chance for ${item.cosmeticName}`);
          downBtn.textContent = "▼";
          downBtn.disabled = localContents.length < 2;
          downBtn.onclick = () => {
            adjustContentProbability(localContents, index, "down");
            rerenderContents();
          };

          probBtns.appendChild(upBtn);
          probBtns.appendChild(downBtn);
          tdAct.appendChild(probBtns);

          const removeBtn = document.createElement("button");
          removeBtn.type = "button";
          removeBtn.className = "btn";
          removeBtn.textContent = "Remove";
          removeBtn.onclick = () => {
            const idx = localContents.indexOf(item);
            if (idx < 0) return;
            const removedBp = percentToBasisPoints(item.probabilityPercent);
            localContents.splice(idx, 1);
            if (localContents.length) {
              if (removedBp > 0) {
                const bp = localContents.map((entry) => percentToBasisPoints(entry.probabilityPercent));
                const others = bp.map((_, i) => i);
                distributeBasisPoints(bp, others, removedBp, 1);
                fixBasisPointsTotal(bp);
                localContents.forEach((entry, i) => {
                  entry.probabilityPercent = basisPointsToPercent(bp[i]);
                });
              } else {
                equalizeContentsProbabilities(localContents);
              }
            }
            rerenderContents();
          };
          tdAct.appendChild(removeBtn);
          tr.appendChild(tdItem);
          tr.appendChild(tdProb);
          tr.appendChild(tdAct);
          tbody.appendChild(tr);
        });
      };
      rerenderContents();

      const addRow = document.createElement("div");
      addRow.className = "formRow";
      const addSelect = document.createElement("select");
      addSelect.className = "formInput";
      const firstOpt = document.createElement("option");
      firstOpt.value = "";
      firstOpt.textContent = "Add published shop item…";
      addSelect.appendChild(firstOpt);
      cosmetics.forEach((item) => {
        if (localContents.some((entry) => entry.cosmeticId === item.id)) return;
        const o = document.createElement("option");
        o.value = item.id;
        o.textContent = `${item.name} (${item.category})`;
        addSelect.appendChild(o);
      });
      const addBtn = document.createElement("button");
      addBtn.type = "button";
      addBtn.className = "btn";
      addBtn.textContent = "Add";
      addBtn.onclick = () => {
        const selected = cosmetics.find((item) => item.id === addSelect.value);
        if (!selected || localContents.some((entry) => entry.cosmeticId === selected.id)) return;
        localContents.push({
          cosmeticId: selected.id,
          cosmeticName: selected.name,
          cosmeticCategory: selected.category,
          cosmeticImageData: selected.imageData,
          cosmeticOutlineMaskData: selected.outlineMaskData ?? null,
          cosmeticAnimationFrames: selected.animationFrames ?? null,
          cosmeticMatchCursorColor: selected.matchCursorColor,
          cosmeticCursorColorShade: selected.cursorColorShade,
          cosmeticMatchCursorMaskData: selected.matchCursorMaskData ?? null,
          cosmeticMatchCursorMaskFrames: selected.matchCursorMaskFrames ?? null,
          cosmeticNewCursor: !!selected.newCursor,
          probabilityPercent: 0,
        });
        equalizeContentsProbabilities(localContents);
        rerenderContents();
        addSelect.value = "";
      };
      addRow.appendChild(addSelect);
      addRow.appendChild(addBtn);
      card.appendChild(addRow);

      const localStatus = document.createElement("p");
      localStatus.className = "accessHint";
      card.appendChild(localStatus);

      const actions = document.createElement("div");
      actions.className = "rgbRow";
      actions.style.justifyContent = "flex-end";
      actions.style.gap = "8px";

      const editArtBtn = document.createElement("button");
      editArtBtn.type = "button";
      editArtBtn.className = "btn";
      editArtBtn.textContent = "Edit art";
      editArtBtn.onclick = () => openCrateArtEditor(crate);
      actions.appendChild(editArtBtn);

      const saveBtn = document.createElement("button");
      saveBtn.type = "button";
      saveBtn.className = "btn green";
      saveBtn.textContent = "Save";
      saveBtn.onclick = async () => {
        if (!localContents.length) {
          localStatus.textContent = "Add at least one item before saving.";
          return;
        }
        localStatus.textContent = "Saving…";
        try {
          await patchAdminShopCrate({
            id: crate.id,
            name: nameInput.value.trim(),
            unlockType: unlockSelect.value as ShopUnlockType,
            priceDots: unlockSelect.value === "dots" ? Number(priceInput.value) : null,
            unlockAchievementId: unlockSelect.value === "achievement" ? achievementInput.value.trim() : null,
            unlockChallengeId: unlockSelect.value === "challenge" ? challengeInput.value.trim() : null,
            unlockScoreMode: unlockSelect.value === "score" ? modeSelect.value : null,
            unlockScoreValue: unlockSelect.value === "score" ? Number(scoreInput.value) : null,
            contents: localContents.map((item) => ({
              cosmeticId: item.cosmeticId,
              probabilityPercent: Number(item.probabilityPercent || 0),
            })),
          });
          localStatus.textContent = "Saved.";
          await load();
        } catch (e) {
          localStatus.textContent = e instanceof Error ? e.message : "Save failed.";
        }
      };
      actions.appendChild(saveBtn);

      const publishBtn = document.createElement("button");
      publishBtn.type = "button";
      publishBtn.className = "btn green";
      publishBtn.textContent = crate.isPublished ? "Unpublish" : "Publish";
      publishBtn.onclick = async () => {
        if (!crate.isPublished) {
          if (!localContents.length) {
            localStatus.textContent = "Add at least one item before publishing.";
            return;
          }
        }
        localStatus.textContent = crate.isPublished ? "Unpublishing…" : "Publishing…";
        try {
          await patchAdminShopCrate({
            id: crate.id,
            name: nameInput.value.trim(),
            unlockType: unlockSelect.value as ShopUnlockType,
            priceDots: unlockSelect.value === "dots" ? Number(priceInput.value) : null,
            unlockAchievementId: unlockSelect.value === "achievement" ? achievementInput.value.trim() : null,
            unlockChallengeId: unlockSelect.value === "challenge" ? challengeInput.value.trim() : null,
            unlockScoreMode: unlockSelect.value === "score" ? modeSelect.value : null,
            unlockScoreValue: unlockSelect.value === "score" ? Number(scoreInput.value) : null,
            contents: localContents.map((item) => ({
              cosmeticId: item.cosmeticId,
              probabilityPercent: Number(item.probabilityPercent || 0),
            })),
            isPublished: !crate.isPublished,
          });
          localStatus.textContent = crate.isPublished ? "Unpublished." : "Published.";
          await load();
        } catch (e) {
          localStatus.textContent = e instanceof Error ? e.message : "Publish failed.";
        }
      };
      actions.appendChild(publishBtn);

      const deleteBtn = document.createElement("button");
      deleteBtn.type = "button";
      deleteBtn.className = "btn";
      deleteBtn.textContent = "Delete";
      deleteBtn.onclick = async () => {
        if (!confirm(`Delete crate "${crate.name}" permanently?`)) return;
        localStatus.textContent = "Deleting…";
        try {
          await deleteAdminShopCrate(crate.id);
          await load();
        } catch (e) {
          localStatus.textContent = e instanceof Error ? e.message : "Delete failed.";
        }
      };
      actions.appendChild(deleteBtn);

      card.appendChild(actions);
      list.appendChild(card);
    }
  }

  async function load() {
    status.textContent = "Loading crates…";
    try {
      const [crateRows, cosmeticRows] = await Promise.all([
        fetchAdminShopCrates(),
        fetchAdminShopCosmetics(),
      ]);
      crates = crateRows;
      cosmetics = cosmeticRows;
      render();
      status.textContent = `${crates.length} crate(s).`;
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Load failed.";
    }
  }

  refreshBtn.onclick = () => {
    if (view === "items") void crateItemsApi.refresh();
    else void load();
  };
  createBtn.onclick = async () => {
    if (view !== "crates") {
      setView("crates");
      return;
    }
    const name = (prompt("Crate name:") || "").trim() || "Untitled Crate";
    status.textContent = "Creating crate…";
    try {
      await publishAdminShopCrate({ name, imageData: "" });
      await load();
      status.textContent = `Created "${name}".`;
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Create failed.";
    }
  };
  crateItemsBtn.onclick = () => setView(view === "items" ? "crates" : "items");
  setView("crates");

  return {
    refresh: () => {
      if (view === "items") void crateItemsApi.refresh();
      else void load();
    },
  };
}

function localContentsLabel(count: number) {
  if (count <= 0) return "No items";
  return count === 1 ? "1 item • 100%" : `${count} items • ${formatPercent(100 / count)}% each`;
}
